import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppSidebar from '../components/common/AppSidebar';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/userService';
import toast from 'react-hot-toast';
import {
  CalendarIcon,
  ClockIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  BellIcon
} from '@heroicons/react/24/outline';

const LegalProfessionalProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    name: '',
    first_name: '',
    last_name: '',
    phone: '',
    bio: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user) {
        navigate('/signin');
        return;
      }
      
      // Check if user is legal professional
      if (user.role_name !== 'legal_professional') {
        navigate('/profile'); // Redirect to regular profile
        return;
      }
      
      try {
        setIsLoading(true);
        const data = await userService.getLegalProfessionalDashboard();
        setDashboardData(data);
        setFormData({
          username: data.profile.username || '',
          email: data.profile.email || '',
          name: data.profile.name || '',
          first_name: data.profile.first_name || '',
          last_name: data.profile.last_name || '',
          phone: data.profile.phone || '',
          bio: data.profile.bio || ''
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [user, navigate]);

  // Focus input when editing field changes
  useEffect(() => {
    if (editingField && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editingField]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const startEditing = (fieldName) => {
    setEditingField(fieldName);
  };
  
  const cancelEditing = () => {
    if (dashboardData && editingField) {
      setFormData(prev => ({
        ...prev,
        [editingField]: dashboardData.profile[editingField] || ''
      }));
    }
    setEditingField(null);
  };
  
  const saveField = async (fieldName) => {
    if (!fieldName) return;
    
    setIsSaving(true);
    
    try {
      const fieldData = { [fieldName]: formData[fieldName] };
      await userService.updateProfile(fieldData);
      
      // Update local data
      setDashboardData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [fieldName]: formData[fieldName]
        }
      }));
      
      setEditingField(null);
      toast.success(`${fieldName.replace('_', ' ')} updated successfully`);
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      toast.error(`Failed to update ${fieldName.replace('_', ' ')}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveField(fieldName);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const getGreeting = () => {
    if (!dashboardData?.profile) return "Legal Professional Dashboard";
    
    const profile = dashboardData.profile;
    const displayName = profile.name || 
      (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : null);
    
    if (!displayName) return "Legal Professional Dashboard";
    
    return `Welcome, ${displayName}`;
  };

  const getVerificationStatus = () => {
    const status = dashboardData?.professional?.verificationStatus;
    switch (status) {
      case 'verified':
        return {
          text: 'Verified Practice',
          icon: CheckCircleIcon,
          className: 'text-green-600 bg-green-100'
        };
      case 'pending':
        return {
          text: 'Verification Pending',
          icon: ClockIcon,
          className: 'text-yellow-600 bg-yellow-100'
        };
      default:
        return {
          text: 'Not Claimed',
          icon: ExclamationTriangleIcon,
          className: 'text-red-600 bg-red-100'
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const renderField = (fieldName, label, multiline = false) => {
    const isEditing = editingField === fieldName;
    const value = dashboardData?.profile ? (dashboardData.profile[fieldName] || '') : '';
    
    return (
      <div className="legal-profile__detail-item">
        <div className="legal-profile__detail-header">
          <h3>{label}</h3>
          {!isEditing && (
            <button 
              onClick={() => startEditing(fieldName)}
              className="legal-profile__edit-field-button"
              aria-label={`Edit ${label}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
              </svg>
            </button>
          )}
        </div>
        
        {isEditing ? (
          <div className="legal-profile__field-edit">
            {multiline ? (
              <textarea
                ref={inputRef}
                name={fieldName}
                value={formData[fieldName]}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, fieldName)}
                rows="4"
                className="legal-profile__edit-input"
              />
            ) : (
              <input
                ref={inputRef}
                type={fieldName === 'email' ? 'email' : fieldName === 'phone' ? 'tel' : 'text'}
                name={fieldName}
                value={formData[fieldName]}
                onChange={handleInputChange}
                onKeyDown={(e) => handleKeyDown(e, fieldName)}
                className="legal-profile__edit-input"
              />
            )}
            <div className="legal-profile__field-actions">
              <button 
                onClick={cancelEditing}
                className="legal-profile__field-button legal-profile__field-button--cancel"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                onClick={() => saveField(fieldName)}
                className="legal-profile__field-button legal-profile__field-button--save"
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <p>{value || 'Not provided'}</p>
        )}
      </div>
    );
  };

  const renderOverviewTab = () => {
    const stats = dashboardData?.statistics;
    const verificationStatus = getVerificationStatus();
    const StatusIcon = verificationStatus.icon;

    return (
      <div className="legal-profile__overview">
        {/* Statistics Cards */}
        <div className="legal-profile__stats-grid">
          <div className="legal-profile__stat-card">
            <div className="legal-profile__stat-icon">
              <CalendarIcon className="h-6 w-6" />
            </div>
            <div className="legal-profile__stat-content">
              <h3>Total Appointments</h3>
              <p className="legal-profile__stat-number">{stats?.appointments?.total_appointments || 0}</p>
              <span className="legal-profile__stat-label">
                {stats?.appointments?.monthly_appointments || 0} this month
              </span>
            </div>
          </div>

          <div className="legal-profile__stat-card">
            <div className="legal-profile__stat-icon">
              <DocumentTextIcon className="h-6 w-6" />
            </div>
            <div className="legal-profile__stat-content">
              <h3>Total Enquiries</h3>
              <p className="legal-profile__stat-number">{stats?.enquiries?.total_enquiries || 0}</p>
              <span className="legal-profile__stat-label">
                {stats?.enquiries?.monthly_enquiries || 0} this month
              </span>
            </div>
          </div>

          <div className="legal-profile__stat-card">
            <div className="legal-profile__stat-icon">
              <ClockIcon className="h-6 w-6" />
            </div>
            <div className="legal-profile__stat-content">
              <h3>Upcoming</h3>
              <p className="legal-profile__stat-number">{stats?.appointments?.upcoming_appointments || 0}</p>
              <span className="legal-profile__stat-label">
                {stats?.appointments?.today_appointments || 0} today
              </span>
            </div>
          </div>

          <div className="legal-profile__stat-card">
            <div className="legal-profile__stat-icon">
              <BellIcon className="h-6 w-6" />
            </div>
            <div className="legal-profile__stat-content">
              <h3>New Enquiries</h3>
              <p className="legal-profile__stat-number">{stats?.enquiries?.new_enquiries || 0}</p>
              <span className="legal-profile__stat-label">
                {stats?.enquiries?.weekly_enquiries || 0} this week
              </span>
            </div>
          </div>
        </div>

        {/* Professional Status */}
        <div className="legal-profile__section">
          <h2>Professional Status</h2>
          <div className="legal-profile__status-card">
            <div className={`legal-profile__status-badge ${verificationStatus.className}`}>
              <StatusIcon className="h-5 w-5" />
              <span>{verificationStatus.text}</span>
            </div>
            
            {dashboardData?.professional?.claimedOrganisation ? (
              <div className="legal-profile__org-details">
                <h3>{dashboardData.professional.claimedOrganisation.practice_name}</h3>
                {dashboardData.professional.claimedOrganisation.sra_number && (
                  <p>SRA Number: {dashboardData.professional.claimedOrganisation.sra_number}</p>
                )}
                {dashboardData.professional.claimedOrganisation.website && (
                  <p>Website: {dashboardData.professional.claimedOrganisation.website}</p>
                )}
              </div>
            ) : (
              <div className="legal-profile__no-org">
                <p>No organisation claimed yet</p>
                <button className="legal-profile__claim-button">
                  Claim Your Practice
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="legal-profile__section">
          <h2>Recent Activity</h2>
          <div className="legal-profile__activity-list">
            {dashboardData?.practice?.recentActivity?.length > 0 ? (
              dashboardData.practice.recentActivity.map((activity, index) => (
                <div key={`${activity.type}-${activity.id}-${index}`} className="legal-profile__activity-item">
                  <div className="legal-profile__activity-icon">
                    {activity.type === 'appointment' ? (
                      <CalendarIcon className="h-5 w-5" />
                    ) : (
                      <DocumentTextIcon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="legal-profile__activity-content">
                    <p>
                      <strong>{activity.type === 'appointment' ? 'Appointment' : 'Enquiry'}</strong> from {activity.user_name}
                    </p>
                    <span className="legal-profile__activity-time">
                      {formatDateTime(activity.created_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="legal-profile__empty-state">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProfileTab = () => (
    <div className="legal-profile__profile-section">
      <h2>Personal Information</h2>
      <div className="legal-profile__details">
        {renderField('username', 'Username')}
        {renderField('email', 'Email')}
        {renderField('name', 'Display Name')}
        {renderField('first_name', 'First Name')}
        {renderField('last_name', 'Last Name')}
        {renderField('phone', 'Phone Number')}
        {renderField('bio', 'About Me', true)}
        
        <div className="legal-profile__detail-item">
          <h3>Account Type</h3>
          <p>Legal Professional</p>
        </div>
        
        <div className="legal-profile__detail-item">
          <h3>Member Since</h3>
          <p>{formatDate(dashboardData?.profile?.created_at)}</p>
        </div>
      </div>
    </div>
  );

  const renderAppointmentsTab = () => (
    <div className="legal-profile__appointments-section">
      <h2>Appointments</h2>
      {dashboardData?.practice?.appointments?.length > 0 ? (
        <div className="legal-profile__appointments-list">
          {dashboardData.practice.appointments.map((appointment) => (
            <div key={appointment.id} className="legal-profile__appointment-card">
              <div className="legal-profile__appointment-header">
                <div className="legal-profile__appointment-status">
                  <span className={`legal-profile__status-badge legal-profile__status-badge--${appointment.status}`}>
                    {appointment.status}
                  </span>
                </div>
                <div className="legal-profile__appointment-date">
                  {formatDate(appointment.appointment_date)} at {appointment.start_time}
                </div>
              </div>
              <div className="legal-profile__appointment-details">
                <div className="legal-profile__appointment-client">
                  <UserIcon className="h-4 w-4" />
                  <span>{appointment.user_name}</span>
                </div>
                <div className="legal-profile__appointment-contact">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>{appointment.user_email}</span>
                </div>
                <div className="legal-profile__appointment-contact">
                  <PhoneIcon className="h-4 w-4" />
                  <span>{appointment.user_phone}</span>
                </div>
              </div>
              {appointment.notes && (
                <div className="legal-profile__appointment-notes">
                  <p>{appointment.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="legal-profile__empty-state">
          <CalendarIcon className="h-12 w-12" />
          <p>No appointments yet</p>
        </div>
      )}
    </div>
  );

  const renderEnquiriesTab = () => (
    <div className="legal-profile__enquiries-section">
      <h2>Enquiries</h2>
      {dashboardData?.practice?.enquiries?.length > 0 ? (
        <div className="legal-profile__enquiries-list">
          {dashboardData.practice.enquiries.map((enquiry) => (
            <div key={enquiry.id} className="legal-profile__enquiry-card">
              <div className="legal-profile__enquiry-header">
                <div className="legal-profile__enquiry-status">
                  <span className={`legal-profile__status-badge legal-profile__status-badge--${enquiry.priority_status}`}>
                    {enquiry.priority_status}
                  </span>
                </div>
                <div className="legal-profile__enquiry-date">
                  {formatDateTime(enquiry.created_at)}
                </div>
              </div>
              <div className="legal-profile__enquiry-details">
                <div className="legal-profile__enquiry-client">
                  <UserIcon className="h-4 w-4" />
                  <span>{enquiry.user_name}</span>
                </div>
                <div className="legal-profile__enquiry-contact">
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>{enquiry.user_email}</span>
                </div>
                <div className="legal-profile__enquiry-contact">
                  <PhoneIcon className="h-4 w-4" />
                  <span>{enquiry.user_phone}</span>
                </div>
                <div className="legal-profile__enquiry-area">
                  <DocumentTextIcon className="h-4 w-4" />
                  <span>{enquiry.legal_area}</span>
                </div>
              </div>
              {enquiry.description && (
                <div className="legal-profile__enquiry-description">
                  <p>{enquiry.description}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="legal-profile__empty-state">
          <DocumentTextIcon className="h-12 w-12" />
          <p>No enquiries yet</p>
        </div>
      )}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'profile':
        return renderProfileTab();
      case 'appointments':
        return renderAppointmentsTab();
      case 'enquiries':
        return renderEnquiriesTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="legal-profile">
      <SEO 
        fallbackTitle="Legal Professional Dashboard | Zahoot"
        fallbackDescription="Manage your legal practice profile and client interactions on Zahoot."
      />
      
      <AppSidebar userType="lawyer" />
      
      <div className="legal-profile__content">
        <div className="legal-profile__container">
          <div className="legal-profile__header">
            <div className="legal-profile__greeting">
              <h1>{getGreeting()}</h1>
              <p className="legal-profile__welcome-text">
                Manage your practice profile and client interactions
              </p>
            </div>
          </div>
          
          <div className="legal-profile__tabs">
            <button
              className={`legal-profile__tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <ChartBarIcon className="h-5 w-5" />
              Overview
            </button>
            <button
              className={`legal-profile__tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <UserIcon className="h-5 w-5" />
              Profile
            </button>
            <button
              className={`legal-profile__tab ${activeTab === 'appointments' ? 'active' : ''}`}
              onClick={() => setActiveTab('appointments')}
            >
              <CalendarIcon className="h-5 w-5" />
              Appointments ({dashboardData?.statistics?.appointments?.total_appointments || 0})
            </button>
            <button
              className={`legal-profile__tab ${activeTab === 'enquiries' ? 'active' : ''}`}
              onClick={() => setActiveTab('enquiries')}
            >
              <DocumentTextIcon className="h-5 w-5" />
              Enquiries ({dashboardData?.statistics?.enquiries?.total_enquiries || 0})
            </button>
          </div>
          
          <div className="legal-profile__body">
            {isLoading ? (
              <div className="legal-profile__loading">
                <div className="spinner"></div>
                <p>Loading dashboard...</p>
              </div>
            ) : (
              dashboardData && renderTabContent()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalProfessionalProfilePage; 